import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Building2, Loader2 } from "lucide-react";

import { authApi } from "@/api/services";
import { switchActiveOrg } from "@/lib/switchActiveOrg";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

/**
 * Active-organization switcher for users who belong to more than one org.
 * Picking a different org calls POST /auth/switch-organization (membership-
 * verified server-side), swaps the stored tokens for the fresh org-scoped
 * session, and does a full reload so every query + context re-initializes for
 * the new org — no re-login. Renders nothing for single-org users.
 */
export function OrgSwitcher() {
  const [switching, setSwitching] = useState(false);
  const { data } = useQuery({
    queryKey: ["auth", "organizations"],
    queryFn: () => authApi.listOrganizations(),
    staleTime: 300_000,
  });

  const orgs = data?.organizations ?? [];
  if (orgs.length <= 1) return null; // nothing to switch between

  const current = orgs.find((o) => o.is_current) ?? orgs[0];

  const handleSwitch = async (orgId: string) => {
    if (switching || orgId === current.org_id) return;
    setSwitching(true);
    try {
      // Full reload: the simplest correct way to re-scope all cached data,
      // stores and route context to the new org.
      await switchActiveOrg(orgId, "/");
    } catch {
      // Membership/permission failure — stay put; the picker resets to current.
      setSwitching(false);
    }
  };

  return (
    <Select
      value={current.org_id}
      onValueChange={handleSwitch}
      disabled={switching}
    >
      <SelectTrigger
        className="h-8 w-auto max-w-[190px] gap-2 border-[var(--color-border,#e2e8f0)] text-sm"
        aria-label="Switch organization"
      >
        {switching ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
        ) : (
          <Building2 className="h-3.5 w-3.5 shrink-0 opacity-70" />
        )}
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {orgs.map((o) => (
          <SelectItem key={o.org_id} value={o.org_id}>
            {o.name}
            {o.is_primary ? " · primary" : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
